import { createSequence } from '@olivierpascal/helpers';
import { Button } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { Pagination } from '~/components/molecules/Pagination';
import {
  PollListItem,
  PollListItemSkeleton,
} from '~/components/molecules/PollListItem';
import { Table } from '~/components/molecules/Table';
import { usePagination } from '~/hooks/usePagination';
import { usePollsQuery } from '~/lib/api/modules/Polls/hooks/usePollsQuery';

const GRID_TEMPLATE_COLUMNS = 'repeat(2, 1fr) repeat(1, min-content)';
const DEFAULT_SKELETON_COUNT = 4;

export type IPollListProps = IContainerProps & {
  appId: string;
};

export const PollList: React.FC<IPollListProps> = (props) => {
  const { sx, appId } = props;
  const { t } = useTranslation();
  const pagination = usePagination();
  const pollsQuery = usePollsQuery(
    {
      appId,
      body: {
        start: pagination.start,
        limit: pagination.limit,
      },
    },
    {
      enabled:
        Number.isInteger(pagination.start) &&
        Number.isInteger(pagination.limit),
      keepPreviousData: true,
    },
  );

  const polls = pollsQuery.data;
  const isEmpty = !pollsQuery.isLoading && !polls?.list?.length;

  return (
    <ContentBlock sx={sx}>
      {pollsQuery.isError ? (
        <ContentBlock.Body first>
          <ErrorMessage error={pollsQuery.error} />
        </ContentBlock.Body>
      ) : isEmpty ? (
        <ContentBlock.Empty title={t('PollList.empty.title')}>
          <Button href={`/apps/${appId}/polls/create`}>
            {t('PollList.empty.actionLabel')}
          </Button>
        </ContentBlock.Empty>
      ) : (
        <>
          <Table gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
            {pollsQuery.isLoading || pollsQuery.isPreviousData
              ? createSequence(DEFAULT_SKELETON_COUNT).map((index) => (
                  <PollListItemSkeleton key={index} first={index === 0} />
                ))
              : polls?.list?.map((poll, pollIndex) => (
                  <PollListItem
                    key={poll._id}
                    poll={poll}
                    first={pollIndex === 0}
                  />
                ))}
          </Table>

          <ContentBlock.Footer>
            <Pagination
              pagination={pagination}
              total={pollsQuery.data?.count}
            />
          </ContentBlock.Footer>
        </>
      )}
    </ContentBlock>
  );
};
